<template>
  <div data-test="custom-timeslot-editor">
    <div v-if="!modelValue.length" class="flex flex-col" :class="{ 'p-error': errorMessage }">
      <div class="flex p-field c-input-required mb-1" data-test="no-timeslots">
        <span class="block text-[14px] font-medium">
          {{ t('sites.stationAppConfig.preOrder.form.dropoff.custom.timeslots.label') }}
        </span>
      </div>
      <span v-if="errorMessage" class="caption" data-test="timeslots-error">
        {{ errorMessage }}
      </span>
    </div>

    <Grid v-else>
      <Col12 v-for="(timeslot, index) in modelValue" :key="index">
        <CustomTimeslotCard
          :timeslot="timeslot"
          :disabled="disabled"
          @edit="openEdit(index)"
          @remove="remove(index)"
        />
      </Col12>
    </Grid>

    <Button
      v-if="!disabled"
      text
      :label="t('sites.stationAppConfig.preOrder.form.dropoff.custom.timeslots.buttonLabel')"
      data-test="add-timeslot"
      @click="openAdd"
    >
      <template #icon>
        <i class="material-symbols-rounded text-[1.5rem] mr-2">add_circle</i>
      </template>
    </Button>

    <!-- Remounted per open so the form snapshots the slot being edited, not the first one. -->
    <CustomTimeslotSidebar
      v-if="sidebarOpen"
      v-model="sidebarOpen"
      :timeslot="editingIndex === null ? null : modelValue[editingIndex]"
      :destinations="destinations"
      @submit="save"
    />
  </div>
</template>

<script setup lang="ts">
import { nextTick, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Button } from '@centric-os/components/src/core/buttons/button';
import { Col12 } from '@centric-os/components/src/core/layouts/columns/col-12';
import { Grid } from '@centric-os/components/src/core/layouts/grids/grid';
import CustomTimeslotCard from './custom-timeslot-card/CustomTimeslotCard.vue';
import CustomTimeslotSidebar from './custom-timeslot-sidebar/CustomTimeslotSidebar.vue';
import type { CustomTimeslot } from './types';

const props = defineProps<{
  modelValue: CustomTimeslot[];
  destinations: string[];
  disabled?: boolean;
  errorMessage?: string;
}>();
const emit = defineEmits<{ 'update:modelValue': [value: CustomTimeslot[]] }>();
const { t } = useI18n();

const sidebarOpen = ref(false);
const editingIndex = ref<number | null>(null);

// The sidebar is not modal, so Add or Edit can be clicked while it is already open. Close it
// first so it remounts and its form snapshots the requested slot rather than the current one.
const open = async (index: number | null): Promise<void> => {
  sidebarOpen.value = false;
  await nextTick();
  editingIndex.value = index;
  sidebarOpen.value = true;
};

const openAdd = (): Promise<void> => open(null);
const openEdit = (index: number): Promise<void> => open(index);

const save = (timeslot: CustomTimeslot): void => {
  const next = [...props.modelValue];
  if (editingIndex.value === null) next.push(timeslot);
  else next[editingIndex.value] = timeslot;
  emit('update:modelValue', next);
};

const remove = (index: number): void => {
  emit(
    'update:modelValue',
    props.modelValue.filter((_, i) => i !== index),
  );
};
</script>
